import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { loadStripe } from "@stripe/stripe-js";
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from "@stripe/react-stripe-js";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useT, LanguageSwitcher } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ArrowLeft, Coins, Spinner } from "@phosphor-icons/react";

const PAYPAL_BUTTON_STYLE = { layout: "vertical", color: "gold", shape: "pill", label: "paypal" };

export default function Billing() {
  const { user, refresh } = useAuth();
  const t = useT();
  const nav = useNavigate();
  const [packages, setPackages] = useState([]);
  const [unitPrice, setUnitPrice] = useState(0.5);
  const [qty, setQty] = useState(10);
  const [stripePromise, setStripePromise] = useState(null);
  const [paypalClientId, setPaypalClientId] = useState("");
  const [checkout, setCheckout] = useState(null); // { lookup_key, quantity, tokens, cost }

  useEffect(() => {
    api.get("/payments/packages").then(({ data }) => {
      setPackages(data.packages || []);
      setUnitPrice(data.unit_price_eur ?? 0.5);
      if (data.publishable_key) setStripePromise(loadStripe(data.publishable_key));
      if (data.paypal_client_id) setPaypalClientId(data.paypal_client_id);
    });
    refresh();
    /* eslint-disable-next-line */
  }, []);

  const openCheckout = (lookup_key, quantity, tokens, cost) => {
    if (!stripePromise) { toast.error("Stripe non disponibile"); return; }
    setCheckout({ lookup_key, quantity, tokens, cost });
  };

  const fetchClientSecret = useCallback(async () => {
    const { data } = await api.post("/payments/checkout", {
      lookup_key: checkout.lookup_key,
      quantity: checkout.quantity,
      ui_mode: "embedded",
      origin_url: window.location.origin,
    });
    return data.client_secret;
  }, [checkout]);

  const options = useMemo(() => ({ fetchClientSecret }), [fetchClientSecret]);
  const paypalOptions = useMemo(() => ({ clientId: paypalClientId, currency: "EUR", intent: "capture" }), [paypalClientId]);

  const best = "tokens_200";

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border">
        <div className="max-w-5xl mx-auto px-6 h-16 flex items-center justify-between">
          <Button variant="ghost" size="icon" onClick={() => nav("/dashboard")} data-testid="billing-back"><ArrowLeft size={18} /></Button>
          <div className="flex items-center gap-3">
            <LanguageSwitcher />
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-md border border-border bg-card">
              <Coins weight="fill" className="text-gold" size={18} />
              <span className="font-monot text-sm font-semibold tabular" data-testid="billing-balance">{user?.tokens_balance ?? 0}</span>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-14">
        <h1 className="text-4xl font-extrabold">{t("bill.title")}</h1>
        <p className="text-muted-foreground mt-2">{t("bill.sub")}</p>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-10">
          <div className="relative border border-chung rounded-lg p-6 bg-card flex flex-col" data-testid="package-custom">
            <div className="text-sm text-muted-foreground uppercase tracking-widest">{t("bill.custom")}</div>
            <input type="number" min={1} max={1000} value={qty} data-testid="custom-qty"
              onChange={(e) => setQty(Math.max(1, Math.min(1000, parseInt(e.target.value) || 1)))}
              style={{ colorScheme: "dark" }}
              className="mt-2 w-full bg-background border border-border rounded-md px-3 py-2 font-score text-5xl text-white" />
            <div className="text-xs text-muted-foreground mt-1 uppercase tracking-widest">{t("bill.token")}</div>
            <div className="mt-4 text-3xl font-bold" data-testid="custom-cost">€{(qty * unitPrice).toFixed(2)}</div>
            <div className="text-xs text-muted-foreground">€{unitPrice.toFixed(2)} {t("bill.perToken")}</div>
            <Button className="mt-6 font-semibold" onClick={() => openCheckout("token_unit", qty, qty, qty * unitPrice)} data-testid="buy-custom">
              {t("bill.buy")}
            </Button>
          </div>
          {packages.map((p) => (
            <div key={p.lookup_key} className={`relative border rounded-lg p-6 bg-card flex flex-col ${p.lookup_key === best ? "border-gold" : "border-border"}`} data-testid={`package-${p.lookup_key}`}>
              {p.lookup_key === best && <span className="absolute -top-2.5 left-4 bg-gold text-black text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded">{t("bill.best")}</span>}
              <div className="font-score text-5xl text-white">{p.tokens}</div>
              <div className="text-sm text-muted-foreground uppercase tracking-widest">{t("bill.token")}</div>
              <div className="mt-4 text-3xl font-bold">€{p.price_eur}</div>
              <div className="text-xs text-muted-foreground">€{(p.price_eur / p.tokens).toFixed(2)} {t("bill.perToken")}</div>
              <Button className="mt-6 font-semibold" onClick={() => openCheckout(p.lookup_key, 1, p.tokens, p.price_eur)} data-testid={`buy-${p.lookup_key}`}>
                {t("bill.buy")}
              </Button>
            </div>
          ))}
        </div>
        <p className="text-xs text-muted-foreground mt-8">{t("bill.secureLive")}</p>
      </main>

      <Dialog open={!!checkout} onOpenChange={(o) => { if (!o) setCheckout(null); }}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="embedded-checkout-dialog">
          <DialogHeader>
            <DialogTitle>{t("bill.checkoutTitle")}</DialogTitle>
            <DialogDescription>
              {checkout ? `${checkout.tokens} ${t("bill.checkoutSub")}${checkout.cost.toFixed(2)}` : ""}
            </DialogDescription>
          </DialogHeader>
          {checkout && stripePromise && (
            <div id="embedded-checkout" data-testid="embedded-checkout-mount">
              <EmbeddedCheckoutProvider stripe={stripePromise} options={options}>
                <EmbeddedCheckout />
              </EmbeddedCheckoutProvider>
            </div>
          )}
          {!stripePromise && (
            <div className="flex items-center gap-2 text-muted-foreground py-8 justify-center">
              <Spinner className="animate-spin" size={20} /> {t("bill.loading")}
            </div>
          )}
          {checkout && paypalClientId && (
            <div className="mt-4" data-testid="paypal-section">
              <div className="flex items-center gap-3 my-4">
                <div className="h-px flex-1 bg-border" />
                <span className="text-xs uppercase tracking-widest text-muted-foreground">{t("bill.orPaypal")}</span>
                <div className="h-px flex-1 bg-border" />
              </div>
              <PayPalScriptProvider options={paypalOptions}>
                <PayPalButtons
                  style={PAYPAL_BUTTON_STYLE}
                  forceReRender={[checkout.lookup_key, checkout.quantity]}
                  createOrder={async () => {
                    const { data } = await api.post("/payments/paypal/create-order", {
                      lookup_key: checkout.lookup_key,
                      quantity: checkout.quantity,
                    });
                    return data.order_id;
                  }}
                  onApprove={async (data) => {
                    try {
                      const { data: res } = await api.post(`/payments/paypal/capture/${data.orderID}`);
                      if (res.credited || res.payment_status === "paid") {
                        toast.success(t("bill.paypalSuccess"));
                        await refresh();
                        setCheckout(null);
                      } else {
                        toast.error(t("bill.paypalError"));
                      }
                    } catch (e) {
                      toast.error(formatApiError(e.response?.data?.detail) || t("bill.paypalError"));
                    }
                  }}
                  onError={() => toast.error(t("bill.paypalError"))}
                />
              </PayPalScriptProvider>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
